import { AxiosResponse } from "axios";
import idx from "idx";
import { isNil } from "ramda";
import { Action } from "redux-actions";
import { call, put, select } from "redux-saga/effects";
import { IAction } from "reduxsauce";

import { IImportReport } from "../../api/CourseAuthorApi";
import { AxiosInterceptedResult } from "../../api/interceptors";
import { IResolveResponse } from "../../api/ResolveApi/resolve-api.types";
import { IState } from "../../App/reducer";
import {
  IClientPatch,
  IPatchAction,
  IPatchAddAction,
  IPatchMoveAction,
  IPatchPayload,
  IPatchSubstAction,
  PatchType,
} from "../../components/course-author/helpers/patches";
import {
  addPatch,
  createPatchBase,
  patchPayloadCreator,
  substPatch,
} from "../../components/course-author/helpers/patches/creators";
import { chooseSingleChoiceAnswerPatch } from "../../components/course-author/helpers/patches/single-choice";
import {
  IFullCourse,
  IListItem,
  ILocalizedListItem,
  ILocalizedListItemAnswer,
} from "../../components/course-author/types";
import { api } from "../../dependencies";
import { SagaAction } from "../../sagas/types";
import { accountId as currentAccount, currentCourse, currentCourseId, locationSearch } from "../../selectors/selectors";
import { getPositionPart } from "../helpers/course";
import { SagaFunction } from "../types";
import {
  patchCourseFailure,
  patchCourseImportTranslationsFailure,
  patchCourseImportTranslationsSuccess,
  patchCourseRequest,
  patchCourseSuccess,
} from "./common";
import { addItemToExercise, patchCourseAndFetch, positionQueryParameter } from "./helpers";
import { IPatchCourseFurtherActions } from "./patches";
import {
  IAddListItemPayload,
  IAddOrderItemPayload,
  IChangeListItemAnswersPayload,
  IChooseSingleChoiceAnswerPayload,
  IColumnPayload,
  ISortPartPayload,
} from "./types";

// NOTE:
// We need to review this implementation again since it brings the following
// consequences:
// - Publish button in side menu is not updated (requires latest changes)
// - Changes list next to Course settings is not updated (requires latest changes)
export const simplePatchCourse: (fa: IPatchCourseFurtherActions) => SagaFunction = furtherActions =>
  function* _patchCourse(action: IAction): SagaAction {
    const { patchPayload } = action;
    const courseId: string = action.courseId || (yield select(currentCourseId));

    yield put(patchCourseRequest());

    try {
      yield call(patchCourseAndFetch, patchPayload);
      yield put(furtherActions.onSuccess(courseId));
    } catch (error) {
      yield put(
        furtherActions.onFailure({
          message: `Failed to patch course ${courseId}`,
        })
      );
    }
  };

export function* sortPartAsync(action: Action<ISortPartPayload>): SagaAction {
  try {
    yield put(patchCourseRequest());

    const {
      payload: { from, to, itemId },
    } = action;
    const state: IState = yield select();
    const accountId = idx(state, ({ App }) => App._.user.accountId) || "";
    const course: IFullCourse = yield select(currentCourse);
    const versionId = course.versionId;

    const clientPatch: IClientPatch<IPatchMoveAction> = {
      ...createPatchBase(accountId, versionId + 1),
      patch: {
        action: {
          from,
          to,
          type: PatchType.move,
        },
        position: getSortPosition(state, itemId),
      },
    };
    const patchPayload: IPatchPayload = {
      baseVersionId: versionId,
      clientPatches: [clientPatch],
    };

    yield call(patchCourseAndFetch, patchPayload);
  } catch (error) {
    yield put(patchCourseFailure(String(error.message)));
  }
}

export function* sortColumn(action: Action<IColumnPayload>): SagaAction {
  try {
    yield put(patchCourseRequest());
    const {
      payload: { from, to, itemId },
    } = action;
    const accountId: string = yield select(currentAccount);
    const course: IFullCourse = yield select(currentCourse);
    const versionId = course.versionId;
    const partPosition: string = yield select(getSortPosition);
    const position = `${partPosition}.${itemId}.columns`;

    const clientPatch: IClientPatch<IPatchMoveAction> = {
      ...createPatchBase(accountId, versionId + 1),
      patch: {
        action: {
          from,
          to,
          type: PatchType.move,
        },
        position,
      },
    };
    const patchPayload: IPatchPayload = {
      baseVersionId: versionId,
      clientPatches: [clientPatch],
    };

    yield call(patchCourseAndFetch, patchPayload);
  } catch (error) {
    yield put(patchCourseFailure(String(error.message)));
  }
}

function getSortPosition(state: IState, itemId?: number): string {
  const locationSearchParams = idx(state, ({ router }) => router.location.search) || "";
  const currentPosition = positionQueryParameter(locationSearchParams);
  const type: string = window.location.search.includes("test") ? "questionBank" : "parts";
  const position: string = `${currentPosition}.${type}`;

  if (!isNil(itemId)) {
    return `${position}.${itemId}.items`;
  }

  return position;
}

export function* chooseSingleChoiceAnswerAsync(action: Action<IChooseSingleChoiceAnswerPayload>): SagaAction {
  try {
    yield put(patchCourseRequest());

    const { payload } = action;
    const { exercise } = payload;
    const positionQueryParam = yield select(locationSearch);
    const currentPosition = positionQueryParameter(positionQueryParam) || "";
    const positionPart = getPositionPart(currentPosition);
    const initialPosition = `${currentPosition}.${positionPart}.${exercise.id}.items`;
    const course: IFullCourse = yield select(currentCourse);
    const versionId = course.versionId;
    const userId: string = yield select(currentAccount);
    const patchPayload: IPatchPayload = patchPayloadCreator(
      versionId,
      chooseSingleChoiceAnswerPatch(payload, userId, versionId, initialPosition)
    );

    yield call(patchCourseAndFetch, patchPayload);
  } catch (error) {
    yield put(patchCourseFailure(String(error.message)));
  }
}

export function* addListItemAsync(action: Action<IAddListItemPayload>): SagaAction {
  try {
    yield put(patchCourseRequest());

    const { exercise } = action.payload;
    const item: ILocalizedListItem = {
      answers: {
        elems: [],
        nextId: 0,
      },
      hint: {
        elems: [],
        nextId: 0,
      },
      id: exercise.content.items.nextId,
      sort: 0,
    };
    const patchPayload: IPatchPayload = yield call(addItemToExercise, item, exercise.id, exercise.content.items.nextId);
    yield call(patchCourseAndFetch, patchPayload);
  } catch (error) {
    yield put(patchCourseFailure(String(error.message)));
  }
}

export function* changeListItemAnswersAsync(action: Action<IChangeListItemAnswersPayload>): SagaAction {
  try {
    yield put(patchCourseRequest());

    const { exercise, listItemId, locale, newAnswers } = action.payload;
    const accountId: string = yield select(currentAccount);
    const course: IFullCourse = yield select(currentCourse);
    const locationSearchParams = yield select(locationSearch);
    const versionId = course.versionId;
    const currentPosition: string = positionQueryParameter(locationSearchParams) || "";
    const positionPart: string = getPositionPart(currentPosition);
    const position: string = `${currentPosition}.${positionPart}.${exercise.id}.items.${listItemId}.answers`;
    const {
      content: {
        items: { elems: exerciseItems },
      },
    } = exercise;
    const indexedItem = exerciseItems.find(i => i.id === listItemId);

    if (!indexedItem) {
      throw new Error("Item index doesn't exist");
    }

    const localizedAnswers = indexedItem.answers.elems.find(a => a.locale === locale);
    const getAnswersPatch = (): Array<IClientPatch<IPatchAction>> => {
      if (localizedAnswers) {
        const oldAnswers = localizedAnswers.value.value;
        const clientPatch: IClientPatch<IPatchSubstAction<string[]>> = {
          ...createPatchBase(accountId, versionId + 1),
          patch: substPatch<string[]>(oldAnswers, newAnswers, `${position}.${localizedAnswers.id}.value.value`),
        };
        return [clientPatch];
      } else {
        const id = indexedItem.answers.nextId;
        const item: ILocalizedListItemAnswer = {
          id,
          locale,
          value: { value: newAnswers },
        };
        const clientPatch: IClientPatch<IPatchAddAction<ILocalizedListItemAnswer>> = {
          ...createPatchBase(accountId, versionId + 1),
          patch: addPatch<ILocalizedListItemAnswer>(item, id, position),
        };
        return [clientPatch];
      }
    };

    const clientPatches = getAnswersPatch();
    const patchPayload: IPatchPayload = {
      baseVersionId: versionId,
      clientPatches,
    };

    yield call(patchCourseAndFetch, patchPayload);
  } catch (error) {
    yield put(patchCourseFailure(String(error.message)));
  }
}

export function* addOrderItemAsync({ payload: { exercise } }: Action<IAddOrderItemPayload>): SagaAction {
  try {
    yield put(patchCourseRequest());
    const id = exercise.content.items.nextId;
    const item: IListItem = {
      hint: {
        elems: [],
        nextId: 0,
      },
      id,
      sort: 0,
      text: {
        elems: [],
        nextId: 0,
      },
    };
    const patchPayload: IPatchPayload = yield call(addItemToExercise, item, exercise.id, id);
    yield call(patchCourseAndFetch, patchPayload);
  } catch (error) {
    yield put(patchCourseFailure(String(error.message)));
  }
}

export function* importTranslationsCsvAsync({ payload: file }: Action<File>): SagaAction {
  try {
    yield put(patchCourseRequest());
    const courseId: string = yield select(currentCourseId);
    const accountId: string = yield select(currentAccount);

    const [uploadError, uploadResponse]: AxiosInterceptedResult<IImportReport> = yield call(
      api.courseAuthor.uploadTranslationsCsv,
      courseId,
      accountId,
      file
    );
    if (uploadError) {
      return yield put(patchCourseImportTranslationsFailure(uploadError));
    }

    const importReport = (uploadResponse as AxiosResponse<IImportReport>).data;
    yield put(patchCourseImportTranslationsSuccess(importReport));

    if (importReport.parsingFailure) {
      const course: IFullCourse = yield select(currentCourse);
      return yield put(patchCourseSuccess(course));
    }

    const [fetchError, response]: AxiosInterceptedResult<Array<IResolveResponse<IFullCourse>>> = yield call(
      api.resolve.courseFull.post,
      accountId,
      [courseId]
    );

    if (fetchError) {
      return yield put(patchCourseImportTranslationsFailure(fetchError));
    }

    yield put(patchCourseSuccess(response!.data[0].value));
  } catch (error) {
    yield put(patchCourseImportTranslationsFailure(String(error.message)));
  }
}
